import {
    TopBetsMatchListActions,
    TopBetsMatchListSetFilterDataAction,
    TopBetsMatchListSetLoadingData,
    TopBetsMatchListStoreModel,
    TopBetsMatchListGroupsOverviewTopic,
} from './top-bets-list.constants';
import { AppDispatch, AppThunk, GetState } from '@om-fe/core/core/types';
import {
    apiSubscribeAction,
    apiUnsubscribeAction,
    attachReducerAction,
    detachReducerAction,
} from '@om-fe/core/utils/action';
import { 
    getTopBetsMatchListSliceName,
    getTopBetsMatchListGroupsOverviewTopic,
} from './top-bets-list.utils';
import { topBetsMatchListGroupsOverviewReducerCreator } from './top-bets-list.reducer';
import { filterNullParams } from '@om-fe/core/utils/router';
import getSlice from '@om-fe/core/utils/get-slice';
import { TimeRangeType } from '../../utils/top-bets.constants';

export const topBetsMatchListSetLoadingData = (slice: string, isLoadingMoreData: boolean = false): TopBetsMatchListSetLoadingData => ({
    type: TopBetsMatchListActions.SetLoadingData,
    meta: {
        slice,
    },
    payload: {
        isLoadingMoreData,
    },
});

export const topBetsMatchGroupsListSetLoadingData = (
    slice: string, 
    isLoadingMoreData: boolean = false
): TopBetsMatchListSetLoadingData => ({
    type: TopBetsMatchListActions.SetLoadingData,
    meta: {
        slice,
    },
    payload: {
        isLoadingMoreData,
    },
});

export const topBetsMatchGroupsOverviewListSetLoadingData = (
    slice: string, 
    isLoadingMoreData: boolean = false
): TopBetsMatchListSetLoadingData => ({
    type: TopBetsMatchListActions.SetLoadingData,
    meta: {
        slice,
    },
    payload: {
        isLoadingMoreData,
    },
});

export const topBetsMatchGroupsOverviewListSetFilterData = (
    slice: string,
    disciplineId: string,
    timeRange: string,
    limit: number,
    marketGroupsOverviewIds?: string,
    isTopBets?: boolean,
    locationId?: string,
    categoryId?: string,
    isCategory?: boolean,
    timeRangeType?: TimeRangeType,
    timeRangeValue?: string,
    isAuthenForTopic?: boolean,
): TopBetsMatchListSetFilterDataAction => ({
    type: TopBetsMatchListActions.SetFilterData,
    meta: {
        slice,
    },
    payload: {
        disciplineId,
        limit,
        timeRange,
        mainMarketsLimit: undefined,
        marketGroupsOverviewIds: marketGroupsOverviewIds!,
        isTopBets,
        locationId,
        categoryId,
        isCategory,
        timeRangeType,
        timeRangeValue,
        isAuthenForTopic,
    },
});

export const getTopBetsMatchGroupsOverviewList = (
    instanceId: string,
    disciplineId: string,
    timeRange: string,
    limit: number,
    marketGroupsOverviewIds: string,
    locationId?: string,
    categoryId?: string,
    isTopBets?: boolean,
    isCategory?: boolean,
    timeRangeType?: TimeRangeType,
    timeRangeValue?: string,
    isAuthenForTopic?: boolean,
) => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getTopBetsMatchListSliceName(instanceId);
    const matchListState: TopBetsMatchListStoreModel | undefined = getSlice<TopBetsMatchListStoreModel>(getState(), slice);
    
    const topic: TopBetsMatchListGroupsOverviewTopic = getTopBetsMatchListGroupsOverviewTopic(
        disciplineId,
        timeRange,
        matchListState?.limit || limit,
        marketGroupsOverviewIds,
        locationId,
        categoryId,
        isTopBets,
        isCategory,
        timeRangeType,
        timeRangeValue,
        isAuthenForTopic,
    );

    dispatch(attachReducerAction(slice, topBetsMatchListGroupsOverviewReducerCreator(instanceId)));

    dispatch(topBetsMatchGroupsOverviewListSetFilterData(
        slice,
        disciplineId,
        timeRange,
        matchListState?.limit || limit,
        marketGroupsOverviewIds,
        isTopBets,
        locationId,
        categoryId,
        isCategory,
        timeRangeType,
        timeRangeValue,
        isAuthenForTopic,
    ));

    dispatch(apiSubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const updateTopBetsMatchGroupsOverviewList = (
    instanceId: string,
    disciplineId: string,
    timeRange: string,
    limit: number,
    marketGroupsOverviewIds: string,
    locationId?: string,
    categoryId?: string,
    isTopBets?: boolean,
    isCategory?: boolean,
    timeRangeType?: TimeRangeType,
    timeRangeValue?: string,
    isAuthenForTopic?: boolean,
) => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getTopBetsMatchListSliceName(instanceId);

    const matchListState: TopBetsMatchListStoreModel | undefined = getSlice<TopBetsMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const oldTopic: TopBetsMatchListGroupsOverviewTopic = getTopBetsMatchListGroupsOverviewTopic(
        matchListState.disciplineId!,
        matchListState.timeRange,
        matchListState.limit,
        matchListState.marketGroupsOverviewIds!,
        matchListState.locationId,
        matchListState.categoryId,
        matchListState.isTopBets,
        matchListState.isCategory,
        matchListState.timeRangeType,
        matchListState.timeRangeValue,
        matchListState.isAuthenForTopic,
    );

    const newTopic: TopBetsMatchListGroupsOverviewTopic = getTopBetsMatchListGroupsOverviewTopic(
        disciplineId,
        timeRange,
        limit,
        marketGroupsOverviewIds,
        locationId,
        categoryId,
        isTopBets,
        isCategory,
        timeRangeType,
        timeRangeValue,
        isAuthenForTopic,
    );

    dispatch(topBetsMatchGroupsOverviewListSetLoadingData(slice, true));
    dispatch(topBetsMatchGroupsOverviewListSetFilterData(
        slice,
        disciplineId,
        timeRange,
        limit,
        marketGroupsOverviewIds,
        isTopBets,
        locationId,
        categoryId,
        isCategory,
        timeRangeType,
        timeRangeValue,
        isAuthenForTopic,
    ));
        
    dispatch(apiUnsubscribeAction(oldTopic.path, filterNullParams(oldTopic.params), slice));
    dispatch(apiSubscribeAction(newTopic.path, filterNullParams(newTopic.params), slice));
};

export const removeTopBetsMatchGroupsOverviewList = (
    instanceId: string,
    noDetach?: boolean,
) => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getTopBetsMatchListSliceName(instanceId);

    const matchListState: TopBetsMatchListStoreModel | undefined = getSlice<TopBetsMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const topic: TopBetsMatchListGroupsOverviewTopic = getTopBetsMatchListGroupsOverviewTopic(
        matchListState.disciplineId!,
        matchListState.timeRange,
        matchListState.limit,
        matchListState.marketGroupsOverviewIds!,
        matchListState.locationId,
        matchListState.categoryId,
        matchListState.isTopBets,
        matchListState.isCategory,
        matchListState.timeRangeType,
        matchListState.timeRangeValue,
        matchListState.isAuthenForTopic,
    );

    if (!noDetach) {
        dispatch(detachReducerAction(slice));
    }
    dispatch(apiUnsubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const addMoreTopBetsMatchList = (instanceId: string, limitNumberOfEvents: number): AppThunk => (
    dispatch: AppDispatch, getState: GetState,
): void => {
    const slice: string = getTopBetsMatchListSliceName(instanceId);

    const matchListState: TopBetsMatchListStoreModel | undefined = getSlice<TopBetsMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const { limit, disciplineId, filteredMarketGroupsOverviewList, timeRange, locationId, categoryId, isTopBets, isCategory, timeRangeType, timeRangeValue, isAuthenForTopic } = matchListState;
    const newEventsNumber = limit + limitNumberOfEvents;
    const groupOverviewsIds = filteredMarketGroupsOverviewList.join(',');

    dispatch(updateTopBetsMatchGroupsOverviewList(instanceId, disciplineId!, timeRange , newEventsNumber, groupOverviewsIds, locationId, categoryId, isTopBets, isCategory, timeRangeType, timeRangeValue, isAuthenForTopic));
};
